import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { TIME_BLOCKS, SUBJECTS, getSubjectColor, TARGET_SCORE, GAOKAO_DATE } from '../lib/constants'
import { getTodayDate } from '../lib/utils'

export default function Dashboard({ userId }) {
  const [stats, setStats] = useState({
    todayBlocks: [],
    totalMinutes: 0,
    avgFocus: 0,
    totalErrors: 0,
    reviewQueue: 0,
  })
  const [loading, setLoading] = useState(true)
  const [gaokaoCountdown, setGaokaoCountdown] = useState(0)
  const today = getTodayDate()

  useEffect(() => {
    // 首次登录同步 profiles 表
    if (userId) {
      supabase.from('profiles').upsert(
        { user_id: userId },
        { onConflict: 'user_id', ignoreDuplicates: true }
      ).then(({ error }) => {
        if (error) console.error('Profile sync error:', error)
      })
    }

    fetchStats()
    const diff = new Date(GAOKAO_DATE) - new Date(today)
    setGaokaoCountdown(Math.ceil(diff / (1000 * 60 * 60 * 24)))
  }, [userId, today])

  const fetchStats = async () => {
    if (!userId) return
    setLoading(true)

    // 今日时段
    const { data: todayData } = await supabase
      .from('gaokao_daily_blocks')
      .select('*')
      .eq('user_id', userId)
      .eq('date', today)
      .eq('completed', true)

    let totalMin = 0
    let totalFocus = 0
    const blocks = todayData || []
    blocks.forEach(b => {
      if (b.start_time && b.end_time) {
        totalMin += Math.floor((new Date(b.end_time) - new Date(b.start_time)) / 60000)
      }
      totalFocus += b.focus_score || 0
    })

    // 错题总数
    const { count: errorCount } = await supabase
      .from('gaokao_error_questions')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)

    // 待复习数
    const { count: reviewCount } = await supabase
      .from('gaokao_knowledge_points')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', userId)
      .lte('next_review_at', today)

    setStats({
      todayBlocks: blocks,
      totalMinutes: totalMin,
      avgFocus: blocks.length ? (totalFocus / blocks.length).toFixed(1) : 0,
      totalErrors: errorCount || 0,
      reviewQueue: reviewCount || 0,
    })
    setLoading(false)
  }

  const subjectStats = {}
  stats.todayBlocks.forEach(b => {
    if (b.subject) {
      subjectStats[b.subject] = (subjectStats[b.subject] || 0) + 1
    }
  })

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">今日概览</h1>
        <p className="text-sm text-ink-500 dark:text-ink-300 mt-1">
          距离高考还有 <span className="text-accent font-bold">{gaokaoCountdown}</span> 天 · 目标分数 {TARGET_SCORE}
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12 text-ink-500">加载中...</div>
      ) : (
        <>
          {/* 核心指标 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold">{stats.todayBlocks.length}</div>
              <div className="text-xs text-ink-500">完成时段</div>
            </div>
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold">{stats.totalMinutes}</div>
              <div className="text-xs text-ink-500">学习分钟</div>
            </div>
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold">{stats.avgFocus}</div>
              <div className="text-xs text-ink-500">专注度</div>
            </div>
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold">{stats.reviewQueue}</div>
              <div className="text-xs text-ink-500">待复习</div>
            </div>
          </div>

          {/* 今日时段详情 */}
          <div className="card p-4 mb-4">
            <h2 className="font-bold mb-3">今日时段</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {TIME_BLOCKS.map(timeBlock => {
                const done = stats.todayBlocks.find(b => b.block_id === timeBlock.id)
                return (
                  <div
                    key={timeBlock.id}
                    className={`px-2 py-1 rounded text-xs text-center ${
                      done?.completed
                        ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                        : 'bg-ink-100 dark:bg-ink-400'
                    }`}
                  >
                    <div className="font-mono">{timeBlock.name}</div>
                    {done?.subject && (
                      <div className="text-xs truncate">{done.subject}</div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* 今日科目分布 */}
          {Object.keys(subjectStats).length > 0 && (
            <div className="card p-4 mb-4">
              <h2 className="font-bold mb-3">今日科目</h2>
              <div className="flex flex-wrap gap-2">
                {Object.entries(subjectStats).map(([subj, count]) => (
                  <span
                    key={subj}
                    className="px-3 py-1 rounded-full text-sm text-white"
                    style={{ backgroundColor: getSubjectColor(subj) }}
                  >
                    {subj} ({count})
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* 快捷入口 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <a href="/tracking" className="card p-4 hover:border-accent transition-all">
              <div className="text-2xl mb-1">⏱</div>
              <div className="font-medium">每日追踪</div>
              <div className="text-xs text-ink-500">开始今天的学习</div>
            </a>
            <a href="/error-book" className="card p-4 hover:border-accent transition-all">
              <div className="text-2xl mb-1">📝</div>
              <div className="font-medium">错题本</div>
              <div className="text-xs text-ink-500">{stats.totalErrors} 道错题</div>
            </a>
            <a href="/review-queue" className="card p-4 hover:border-accent transition-all">
              <div className="text-2xl mb-1">🔄</div>
              <div className="font-medium">遗复</div>
              <div className="text-xs text-ink-500">{stats.reviewQueue} 个待复习</div>
            </a>
            <a href="/review" className="card p-4 hover:border-accent transition-all">
              <div className="text-2xl mb-1">📋</div>
              <div className="font-medium">复盘</div>
              <div className="text-xs text-ink-500">记录今天</div>
            </a>
          </div>
        </>
      )}
    </div>
  )
}
